import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { DICT, RULES } from "@/i18n/translations";

const LANG_KEY = "av_lang";
export const LANGS = [
    ["en", "English"],
    ["es", "Español"],
    ["fr", "Français"],
];

const norm = (s) => s.replace(/\s+/g, " ").trim();
const originals = new WeakMap();
const SKIP = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "INPUT", "SELECT", "OPTION", "CODE", "PRE", "SVG"]);

function lookup(text, lang) {
    const key = norm(text);
    if (!key) return null;
    const entry = DICT[key];
    if (entry && entry[lang]) return entry[lang];
    for (const r of RULES) {
        const m = key.match(r.re);
        if (m && r[lang]) return r[lang].replace(/\$(\d)/g, (_, i) => m[+i]);
    }
    return null;
}

function walk(lang) {
    const tw = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
        acceptNode(n) {
            const p = n.parentElement;
            if (!p || SKIP.has(p.tagName) || p.closest("[data-no-translate]")) {
                return NodeFilter.FILTER_REJECT;
            }
            return NodeFilter.FILTER_ACCEPT;
        },
    });
    const nodes = [];
    while (tw.nextNode()) nodes.push(tw.currentNode);
    for (const n of nodes) {
        const original = originals.has(n) ? originals.get(n) : n.nodeValue;
        if (lang === "en") {
            if (originals.has(n) && n.nodeValue !== original) n.nodeValue = original;
            continue;
        }
        const t = lookup(original, lang);
        if (t != null) {
            if (!originals.has(n)) originals.set(n, n.nodeValue);
            const lead = original.match(/^\s*/)[0];
            const trail = original.match(/\s*$/)[0];
            const next = lead + t + trail;
            if (n.nodeValue !== next) n.nodeValue = next;
        } else if (originals.has(n) && n.nodeValue !== original) {
            n.nodeValue = original;
        }
    }
    for (const el of document.body.querySelectorAll("[placeholder],[title],[aria-label]")) {
        if (el.closest("[data-no-translate]")) continue;
        const store = el.__avI18n || (el.__avI18n = {});
        for (const attr of ["placeholder", "title", "aria-label"]) {
            const cur = el.getAttribute(attr);
            if (cur == null) continue;
            const original = attr in store ? store[attr] : cur;
            if (lang === "en") {
                if (attr in store && cur !== original) el.setAttribute(attr, original);
                continue;
            }
            const t = lookup(original, lang);
            if (t != null) {
                if (!(attr in store)) store[attr] = cur;
                if (cur !== t) el.setAttribute(attr, t);
            } else if (attr in store && cur !== original) {
                el.setAttribute(attr, original);
            }
        }
    }
}

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
    // Default is ALWAYS English — no browser auto-detect (per product decision).
    // localStorage is acceptable here: language is a non-sensitive UI preference.
    const [lang, setLangState] = useState(() => {
        const s = localStorage.getItem(LANG_KEY);
        return s === "es" || s === "fr" ? s : "en";
    });

    const setLang = (l) => {
        localStorage.setItem(LANG_KEY, l);
        setLangState(l);
    };

    useEffect(() => {
        document.documentElement.lang = lang;
        walk(lang);
        if (lang === "en") return undefined;
        const opts = { childList: true, subtree: true, characterData: true };
        let t = null;
        const obs = new MutationObserver(() => {
            clearTimeout(t);
            t = setTimeout(() => {
                // Disconnect while we write so our own mutations never re-trigger.
                obs.disconnect();
                walk(lang);
                obs.observe(document.body, opts);
            }, 120);
        });
        obs.observe(document.body, opts);
        return () => {
            obs.disconnect();
            clearTimeout(t);
        };
    }, [lang]);

    const value = useMemo(() => ({ lang, setLang, langs: LANGS }), [lang]);
    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
    const ctx = useContext(LanguageContext);
    if (!ctx) throw new Error("useLanguage must be used inside LanguageProvider");
    return ctx;
}
